import * as Yup from 'yup';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { Grid } from '@mui/material';
import { PoolButton, PoolModal, PoolTextArea, PoolTextField } from '@synergy/components';
import { fields } from '@synergy/utils';

export interface InquiryDetailOffersTableDeclineModalData {
    inquiryNumber: string;
    inquiryTitle: string;
    shopOwnerName: string;
}

export interface InquiryDetailOffersTableDeclineModalProps {
    data?: InquiryDetailOffersTableDeclineModalData;
    onDecline?: (subject: string, message: string) => void;
    onClose?: () => void;
}

interface FormData {
    subject: string;
    message: string;
}

const fieldNames = fields<FormData>();
const validationSchema = Yup.object().shape({
    [fieldNames.subject]: Yup.string().required('app.error.required'),
    [fieldNames.message]: Yup.string().required('app.error.required'),
});

export const InquiryDetailOffersTableDeclineModal = (props: InquiryDetailOffersTableDeclineModalProps) => {
    const { data } = props;
    const intl = useIntl();

    const handleSubmit = (values: FormData) => {
        const { subject, message } = values;
        props.onDecline?.(subject, message);
        props.onClose?.();
    };

    const handleDialogClose = () => {
        props.onClose?.();
    };

    const initialFormData: FormData = {
        subject: intl.formatMessage(
            { id: 'app.inquiries.detail.action.declineOffer.defaultSubject' },
            {
                number: data?.inquiryNumber,
                title: data?.inquiryTitle,
                shopOwnerName: data?.shopOwnerName,
            }
        ),
        message: intl.formatMessage({ id: 'app.inquiries.detail.action.declineOffer.defaultBody' }),
    };

    return (
        <Formik
            initialValues={initialFormData}
            validationSchema={validationSchema}
            enableReinitialize={true}
            onSubmit={handleSubmit}
        >
            {(formikProps: FormikProps<FormData>) => (
                <PoolModal
                    isVisible={!!data}
                    title={<FormattedMessage id="app.inquiries.detail.action.declineOffer" />}
                    handleClose={handleDialogClose}
                    width="lg"
                    actionChildren={[
                        <PoolButton key="1" variant="text" styleType="firstLevel" onClick={handleDialogClose}>
                            <FormattedMessage id="app.common.cancel" />
                        </PoolButton>,
                        <PoolButton key="2" styleType="firstLevel" color="secondary" onClick={formikProps.submitForm}>
                            <FormattedMessage id="app.inquiries.detail.action.declineOffer" />
                        </PoolButton>,
                    ]}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Field name={fieldNames.subject}>
                                {({ field, meta }: FieldProps<string>) => (
                                    <PoolTextField
                                        {...field}
                                        label={<FormattedMessage id="app.label.subject" />}
                                        fullWidth={true}
                                        required={true}
                                        error={meta.touched && !!meta.error}
                                        helperText={
                                            meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                        }
                                    />
                                )}
                            </Field>
                        </Grid>
                        <Grid item xs={12}>
                            <Field name={fieldNames.message}>
                                {({ field, meta }: FieldProps<string>) => (
                                    <PoolTextArea
                                        {...field}
                                        label={<FormattedMessage id="app.label.text" />}
                                        rows={5}
                                        required={true}
                                        error={meta.touched && !!meta.error}
                                        helperText={
                                            meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                        }
                                    />
                                )}
                            </Field>
                        </Grid>
                    </Grid>
                </PoolModal>
            )}
        </Formik>
    );
};
